import React, { useEffect, useRef, useState } from "react";
import { Trophy, Lock, TrendingUp, TrendingDown } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { getPref, setPref } from "../lib/prefs";

const PREF_KEY = "achievements_earned_v1";

/**
 * "Beat the Benchmark" achievement widget for the Paper page.
 *
 * Surfaces 4 windowed badges (7d, 30d, 90d, all-time) that light up when
 * the user's paper portfolio outperforms the selected benchmark over that
 * window. Earned badges trigger a one-time sonner celebration on first
 * earn (tracked in a cookie pref so refreshes don't re-trigger).
 *
 * Reads the user's currently-selected benchmark symbol from props so it
 * stays in sync with the chart selector above.
 */
export default function BenchmarkAchievements({ symbol = "SPY", assetType = "etf" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const celebratedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/paper/achievements?symbol=${symbol}&asset_type=${assetType}`)
      .then((r) => {
        if (cancelled) return;
        setData(r.data);

        // Celebrate newly-earned badges — read prior earned set from the cookie pref,
        // toast for each badge that's earned now but wasn't before. Skip the very
        // first load of a fresh device (celebratedRef gate) so we don't flood
        // returning users with stale celebrations.
        if (!celebratedRef.current) {
          celebratedRef.current = true;
          try {
            const earnedNow = new Set(r.data.badges.filter((b) => b.earned).map((b) => b.key));
            const prior = new Set(getPref(PREF_KEY, []));
            const fresh = [...earnedNow].filter((k) => !prior.has(k));
            // First-ever load (empty prior set) shouldn't toast — just record.
            if (prior.size > 0) {
              for (const key of fresh) {
                const badge = r.data.badges.find((b) => b.key === key);
                if (badge) {
                  toast.success(`🏆 Achievement unlocked: ${badge.label}`, {
                    description: `You're beating ${r.data.benchmark.symbol} by ${badge.diff_pct.toFixed(2)}% — keep it up.`,
                    duration: 6000,
                  });
                }
              }
            }
            setPref(PREF_KEY, [...earnedNow]);
          } catch (e) {
            console.warn("Achievement celebration check failed:", e);
          }
        }
      })
      .catch((e) => console.warn("Achievements load failed:", e))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [symbol, assetType]);

  if (loading) {
    return (
      <div
        className="rounded-2xl bg-white dark:bg-[#161B22] border border-[#EAE6E1] dark:border-[#2A3340] p-5 animate-pulse h-40"
        data-testid="achievements-loading"
      />
    );
  }

  if (!data || !data.has_history) {
    return (
      <div
        className="rounded-2xl bg-white dark:bg-[#161B22] border border-[#EAE6E1] dark:border-[#2A3340] p-6 text-center"
        data-testid="achievements-empty"
      >
        <Trophy className="w-6 h-6 text-[#6B7068] mx-auto mb-2" strokeWidth={1.5} />
        <div className="text-sm text-[#6B7068] dark:text-[#8B949E]">
          Place a few paper trades to unlock benchmark achievements.
        </div>
      </div>
    );
  }

  const { badges, benchmark, earned_count, total_count } = data;

  return (
    <section
      className="rounded-2xl bg-white dark:bg-[#161B22] border border-[#EAE6E1] dark:border-[#2A3340] p-5 md:p-6"
      data-testid="achievements-widget"
    >
      <header className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#6B7068] dark:text-[#8B949E]">
            Beat the benchmark
          </div>
          <h3 className="font-heading text-lg text-[#2A2F2B] dark:text-[#E6EDF3] mt-0.5">
            vs <span className="font-mono-ticker">{benchmark.symbol}</span> · {benchmark.name}
          </h3>
        </div>
        <div
          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#5C8D7315] text-[#5C8D73] text-xs font-semibold"
          data-testid="achievements-earned-count"
        >
          <Trophy className="w-3.5 h-3.5" strokeWidth={2} />
          {earned_count} / {total_count}
        </div>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {badges.map((b) => {
          const earned = b.earned;
          const diff = b.diff_pct;
          const Icon = earned ? Trophy : Lock;
          const DiffIcon = (diff ?? 0) >= 0 ? TrendingUp : TrendingDown;
          return (
            <div
              key={b.key}
              data-testid={`achievement-${b.key}`}
              data-earned={earned}
              className={`rounded-xl p-3 border transition-soft ${
                earned
                  ? "bg-[#5C8D7315] border-[#5C8D73]/40"
                  : "bg-[#F9F8F6] dark:bg-[#1F2630] border-[#EAE6E1] dark:border-[#2A3340] opacity-70"
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <Icon
                  className={`w-4 h-4 ${earned ? "text-[#5C8D73]" : "text-[#6B7068]"}`}
                  strokeWidth={2}
                />
                <span className="text-xs font-semibold text-[#2A2F2B] dark:text-[#E6EDF3]">
                  {b.label}
                </span>
              </div>
              {diff !== null && diff !== undefined ? (
                <div className={`text-xs font-mono-ticker flex items-center gap-1 ${
                  diff >= 0 ? "text-[#5C8D73]" : "text-[#C66B59]"
                }`}>
                  <DiffIcon className="w-3 h-3" strokeWidth={2} />
                  {diff >= 0 ? "+" : ""}{diff.toFixed(2)}%
                </div>
              ) : (
                <div className="text-xs text-[#6B7068]">No data yet</div>
              )}
              <div className="text-[10px] text-[#6B7068] dark:text-[#8B949E] mt-1">
                You {b.paper_pct >= 0 ? "+" : ""}{b.paper_pct?.toFixed?.(1)}% · {benchmark.symbol} {b.bench_pct >= 0 ? "+" : ""}{b.bench_pct?.toFixed?.(1) ?? "—"}%
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
